import { Injectable } from '@angular/core';
import { StorageKey } from '@core/enums/store/storage-keys.enum';
import { ViewType } from '@core/enums/view-type.enum';
import { AuthToken } from '@core/models/auth/auth-token.model';
import { GuestToken } from '@core/models/auth/guest-token.model';
import { AdminPartner } from '@core/models/iam/admin-partner.model';
import { UserProfile } from '@core/models/user/user-profile.model';
import { AAGUID, Browser, PartnerConfig, SsrService } from '@interticket/core';
import { BehaviorSubject, Subject } from 'rxjs';

@Injectable()
export class StoreService {

  readonly pauseConcurrent$ = new Subject<void>();

  authToken$ = new BehaviorSubject<AuthToken>(this.getItem(StorageKey.AUTH_TOKEN_KEY));
  partnerConfig$ = new BehaviorSubject<PartnerConfig>(null);
  guestToken$ = new BehaviorSubject<GuestToken>((this.getItem(StorageKey.GUEST_TOKEN_KEY)));
  features$ = new BehaviorSubject<string[]>([]);
  userProfile$ = new BehaviorSubject<UserProfile>(null);
  streamableEvents$ = new BehaviorSubject<AAGUID[]>([]);
  isLoadingChunkFailed$ = new BehaviorSubject<boolean>(false);
  permissionError$ = new BehaviorSubject<boolean>(false);
  isConcurrentPlaybackModalOpen$ = new BehaviorSubject<boolean>(false);
  appInitialized$ = new BehaviorSubject<boolean>(false);
  isReloaded$ = new BehaviorSubject<boolean>(this.getSessionItem(StorageKey.IS_RELOADED_KEY));
  availableAdminPartners$ = new BehaviorSubject<AdminPartner[]>([]);
  eventListViewType$ = new BehaviorSubject<ViewType>(this.getItem(StorageKey.EVENT_LIST_VIEW_TYPE_KEY));

  constructor(
    private ssrService: SsrService
  ) { }

  // LocalStorage
  @Browser()
  private setItem<T>(key: StorageKey, value: T): void {
    if (value === null) {
      this.ssrService.localStorage.removeItem(key);
    } else {
      this.ssrService.localStorage.setItem(key, JSON.stringify(value));
    }
  }

  @Browser()
  private getItem<T>(key: StorageKey): T {
    return JSON.parse(this.ssrService.localStorage.getItem(key));
  }

  // SessionStorage
  @Browser()
  private setSessionItem<T>(key: StorageKey, value: T): void {
    if (value === null) {
      this.ssrService.sessionStorage.removeItem(key);
    } else {
      this.ssrService.sessionStorage.setItem(key, JSON.stringify(value));
    }
  }

  @Browser()
  private getSessionItem<T>(key: StorageKey): T {
    return JSON.parse(this.ssrService.sessionStorage.getItem(key));
  }

  // AuthToken
  set authToken(authToken: AuthToken) {
    this.setItem(StorageKey.AUTH_TOKEN_KEY, authToken);
    this.authToken$.next(authToken);
  }

  get authToken(): AuthToken {
    return this.authToken$.getValue();
  }

  clearAuthToken(): void {
    this.setItem(StorageKey.AUTH_TOKEN_KEY, null);
  }

  // Partner config
  set partnerConfig(partnerConfig: PartnerConfig) {
    this.partnerConfig$.next(partnerConfig);
  }

  get partnerConfig(): PartnerConfig {
    return this.partnerConfig$.getValue();
  }

  // Guest token
  set guestToken(guestToken: GuestToken) {
    this.setItem(StorageKey.GUEST_TOKEN_KEY, guestToken);
    this.guestToken$.next(guestToken);
  }

  get guestToken(): GuestToken {
    return this.guestToken$.getValue();
  }

  // Features
  set features(features: string[]) {
    this.features$.next(features);
  }

  get features(): string[] {
    return this.features$.getValue();
  }

  // User profile
  set userProfile(userProfile: UserProfile) {
    this.userProfile$.next(userProfile);
  }

  get userProfile(): UserProfile {
    return this.userProfile$.getValue();
  }

  // Streamable events
  set streamableEvents(events: AAGUID[]) {
    this.streamableEvents$.next(events);
  }

  get streamableEvents(): AAGUID[] {
    return this.streamableEvents$.getValue();
  }

  // Loading chunk failed
  set isLoadingChunkFailed(isFailed: boolean) {
    this.isLoadingChunkFailed$.next(isFailed);
  }

  get isLoadingChunkFailed(): boolean {
    return this.isLoadingChunkFailed$.getValue();
  }

  // Permission error
  set permissionError(isError: boolean) {
    this.permissionError$.next(isError);
  }

  get permissionError(): boolean {
    return this.permissionError$.getValue();
  }

  // App initialized
  set appInitialized(isInitialized: boolean) {
    this.appInitialized$.next(isInitialized);
  }

  get appInitialized(): boolean {
    return this.appInitialized$.getValue();
  }

  // Is page reloaded due to expired guest token
  set isReloaded(isReloaded: boolean) {
    this.setSessionItem(StorageKey.IS_RELOADED_KEY, isReloaded);
    this.isReloaded$.next(isReloaded);
  }

  get isReloaded(): boolean {
    return this.isReloaded$.getValue();
  }

  set availableAdminPartners(availableAdminPartners: AdminPartner[]) {
    this.availableAdminPartners$.next(availableAdminPartners);
  }

  get availableAdminPartners(): AdminPartner[] {
    return this.availableAdminPartners$.getValue();
  }

  set eventListViewType(viewType: ViewType) {
    this.setItem(StorageKey.EVENT_LIST_VIEW_TYPE_KEY, viewType);
    this.eventListViewType$.next(viewType);
  }

  get eventListViewType(): ViewType {
    return this.eventListViewType$.getValue() || ViewType.LIST;
  }

}
